import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, MapPin, CheckCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const BookingModal = ({ doctor, isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: select date, 2: select time, 3: confirm
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const startPadding = (firstDay.getDay() + 6) % 7; // Adjust for Monday start
    
    // Add padding for days before first of month
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    // Add days of month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    
    return days;
  };

  const isDateSelectable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/doctors/${doctor.id}/available-slots?date=${formatDate(date)}`);
      setAvailableSlots(response.data.available_slots || []);
    } catch (error) {
      console.error('Failed to fetch slots', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    await fetchAvailableSlots(date);
    setStep(2);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedSlot) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        doctor_id: doctor.id,
        appointment_date: formatDate(selectedDate) + 'T' + selectedSlot + ':00',
        time_slot: selectedSlot,
        reason: reason || 'Consultation'
      });
      
      toast.success('Rendez-vous réservé avec succès !');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedSlot(null);
    setReason('');
    setAvailableSlots([]);
  };

  useEffect(() => {
    if (!isOpen) {
      resetModal();
    }
  }, [isOpen]);

  const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                     'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-teal-600" />
            <span>Prendre rendez-vous</span>
          </DialogTitle>
        </DialogHeader>

        {/* Doctor Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold">{doctor?.clinic_name}</h3>
              <p className="text-sm text-gray-600">{doctor?.specialization}</p>
              <p className="text-xs text-gray-500 flex items-center">
                <MapPin className="w-3 h-3 mr-1" />
                {doctor?.city}
              </p>
            </div>
          </div>
        </div>

        {/* Step 1: Select Date */}
        {step === 1 && (
          <div data-testid="date-picker">
            <h4 className="font-medium mb-4">Choisissez une date</h4>
            
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <span className="font-semibold">
                {MONTHS_FR[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              >
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {DAYS_FR.map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
              {generateCalendarDays().map((date, idx) => (
                <div key={idx} className="aspect-square">
                  {date && (
                    <button
                      onClick={() => isDateSelectable(date) && handleDateSelect(date)}
                      disabled={!isDateSelectable(date)}
                      className={`w-full h-full rounded-lg text-sm flex items-center justify-center transition-colors
                        ${!isDateSelectable(date) 
                          ? 'text-gray-300 cursor-not-allowed' 
                          : 'hover:bg-teal-100 hover:text-teal-700 cursor-pointer'}
                        ${selectedDate && formatDate(date) === formatDate(selectedDate) 
                          ? 'bg-teal-500 text-white hover:bg-teal-600' 
                          : ''}
                      `}
                    >
                      {date.getDate()}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Time */}
        {step === 2 && (
          <div data-testid="time-picker">
            <div className="flex items-center mb-4">
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-1" />
                Retour
              </Button>
              <h4 className="font-medium ml-2">
                {selectedDate?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </h4>
            </div>

            {loadingSlots ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : availableSlots.length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {availableSlots.map(slot => (
                  <button
                    key={slot}
                    onClick={() => handleSlotSelect(slot)}
                    className={`py-3 px-2 rounded-lg text-sm font-medium transition-colors
                      ${selectedSlot === slot 
                        ? 'bg-teal-500 text-white' 
                        : 'bg-gray-100 hover:bg-teal-100 hover:text-teal-700'}
                    `}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Aucun créneau disponible pour cette date</p>
                <p className="text-sm">Veuillez sélectionner une autre date</p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div data-testid="booking-confirm">
            <div className="flex items-center mb-4">
              <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-1" />
                Retour
              </Button>
              <h4 className="font-medium ml-2">Confirmer le rendez-vous</h4>
            </div>

            <div className="bg-teal-50 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <Calendar className="w-4 h-4 text-teal-600" />
                <span className="font-medium">
                  {selectedDate?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-teal-600" />
                <span className="font-medium">{selectedSlot}</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Motif de la consultation (optionnel)</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Décrivez brièvement la raison de votre visite..."
                rows={3}
              />
            </div>

            <Button 
              onClick={handleBooking} 
              disabled={loading}
              className="w-full btn-primary h-12"
              data-testid="confirm-booking-btn"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Réservation en cours...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Confirmer le rendez-vous
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
